'use client';

import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

const SCROLL_STOP_DELAY_MS = 150;

interface UseScrollActivityInput {
  containerRef: RefObject<HTMLElement | null>;
}

export function useScrollActivity({
  containerRef,
}: UseScrollActivityInput): boolean {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const content = containerRef.current;

    if (!content) {
      return;
    }

    const handleScroll = (): void => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        setIsScrolling(true);

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          setIsScrolling(false);
        }, SCROLL_STOP_DELAY_MS);
      });
    };

    content.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      content.removeEventListener('scroll', handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [containerRef]);

  return isScrolling;
}
